import React from 'react';

import { ArrowLeftOutlined, HomeOutlined, ReloadOutlined } from '@ant-design/icons';
import { Result, Button, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useRouteError, useNavigate, isRouteErrorResponse } from 'react-router-dom';

const { Text } = Typography;

/**
 * RouteErrorBoundary - 專門處理 React Router loader/action 錯誤的組件
 * 使用 useRouteError hook 來獲取路由錯誤
 */
const RouteErrorBoundary = ({ showDetails = false, homeUrl = '/dashboard' }) => {
  const { t } = useTranslation();
  const error = useRouteError();
  const navigate = useNavigate();

  // 判斷錯誤類型
  const errorMessage = error?.message?.toLowerCase() || '';

  // 檢查是否為 404 錯誤
  // - isRouteErrorResponse 的 404 狀態
  // - 錯誤訊息包含 "not found"
  // - 錯誤訊息包含 "failed to fetch" (通常是 loader 無法取得資源)
  const is404 = isRouteErrorResponse(error)
    ? error.status === 404
    : errorMessage.includes('not found') || errorMessage.includes('failed to fetch');

  const is403 = isRouteErrorResponse(error)
    ? error.status === 403
    : errorMessage.includes('forbidden');

  const is401 = isRouteErrorResponse(error)
    ? error.status === 401
    : errorMessage.includes('unauthorized');

  // 根據錯誤類型設置狀態和標題
  let status = 'error';
  let title = t('common.route_error.title', 'Something went wrong');
  let subTitle = t('common.route_error.subtitle', 'An unexpected error occurred');

  if (is404) {
    status = '404';
    title = t('common.route_error.not_found_title', 'Resource Not Found');
    subTitle = t('common.route_error.not_found_subtitle', 'The resource you are looking for does not exist or has been removed.');
  } else if (is403) {
    status = '403';
    title = t('common.route_error.forbidden_title', 'Access Denied');
    subTitle = t('common.route_error.forbidden_subtitle', 'You do not have permission to access this resource.');
  } else if (is401) {
    status = '403';
    title = t('common.route_error.unauthorized_title', 'Unauthorized');
    subTitle = t('common.route_error.unauthorized_subtitle', 'Please log in to continue.');
  }

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate(homeUrl);
  };

  const handleReload = () => {
    window.location.reload();
  };

  // 獲取錯誤詳情
  const getErrorDetails = () => {
    if (isRouteErrorResponse(error)) {
      return `Status: ${error.status}\nStatusText: ${error.statusText}\nData: ${JSON.stringify(error.data, null, 2)}`;
    }
    if (error instanceof Error) {
      return `${error.message}\n\n${error.stack}`;
    }
    return String(error);
  };

  return (
    <div style={{ padding: '50px', textAlign: 'center' }}>
      <Result
        status={status}
        title={title}
        subTitle={subTitle}
        extra={
          <Space direction="vertical" size="middle">
            <Space>
              <Button
                type="primary"
                icon={<ArrowLeftOutlined />}
                onClick={handleGoBack}
              >
                {t('common.route_error.go_back', 'Go Back')}
              </Button>
              <Button
                icon={<HomeOutlined />}
                onClick={handleGoHome}
              >
                {t('common.route_error.go_home', 'Go to Dashboard')}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleReload}
              >
                {t('common.route_error.reload', 'Reload Page')}
              </Button>
            </Space>
          </Space>
        }
      />

      {showDetails && error && (
        <details style={{
          marginTop: '20px',
          textAlign: 'left',
          backgroundColor: '#f5f5f5',
          padding: '16px',
          borderRadius: '8px',
          maxWidth: '800px',
          margin: '20px auto 0'
        }}>
          <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
            <Text type="secondary">
              {t('common.route_error.error_details', 'Error Details')}
            </Text>
          </summary>
          <pre style={{
            whiteSpace: 'pre-wrap',
            fontSize: '12px',
            marginTop: '10px',
            color: '#666',
            backgroundColor: '#fff',
            padding: '12px',
            borderRadius: '4px',
            border: '1px solid #d9d9d9'
          }}>
            {getErrorDetails()}
          </pre>
        </details>
      )}
    </div>
  );
};

export default RouteErrorBoundary;
